import { useTheme } from "@mui/material";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import { FC, useCallback, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationDialog } from "../../../components";
import { DataTable } from "../../../components/data-table/DataTable";
import { DeleteActionCell } from "../../../components/data-table/DeleteActionCell";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import { USER_GROUPS_QUERY_KEY } from "../../../hooks/useAllUserGroupsForACompany";
import { useCompanyData } from "../../../hooks/useCompanyData";
import { useLocalization } from "../../../hooks/useLocalization";
import { Routes } from "../../../types/NavigationPaths";
import { INCOMING_LICENSES_QUERY_KEY } from "../../experiences/hooks/useIncomingLicenses";
import { GroupNameCell, UserGroupRow } from "../../groups/GroupsPage";
import { USERS_IN_GROUP_QUERY_KEY } from "../../groups/hooks/useUsersInGroup";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import { USER_RELEASE_CHANNELS_QUERY_KEY } from "../hooks/useUserExperiences";
import { AddUserToGroupDialog } from "./AddUserToGroupDialog";

interface GroupsTabProps {
  user: UserManagement_V1.UserWithCompanyName;
  userGroups: UserManagement_V1.UserGroup[];
}

const INITIAL_SORTING: SortingState = [{ id: "name", desc: false }];

export const GroupsTab: FC<GroupsTabProps> = ({ userGroups, user }) => {
  const theme = useTheme();
  const userManagementClient = useUserManagementServiceV1();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { t } = useLocalization();

  const handleRowClick = (userGroup: UserGroupRow) => {
    void navigate(`/companies/${companyId}${Routes.GROUPS}/${userGroup.userGroupId}`);
  };

  const [groupToRemove, setGroupToRemove] = useState<UserGroupRow | null>(null);
  const [isRemoveFromGroupDialogOpen, setIsRemoveFromGroupDialogOpen] =
    useState(false);
  const [isAddUserToGroupDialogOpen, setIsAddUserToGroupDialogOpen] =
    useState(false);

  const queryClient = useQueryClient();

  const removeUserFromGroupMutation = useMutation({
    mutationFn: ({
      userGroupId,
      targetUserId,
    }: {
      userGroupId: string;
      targetUserId: string;
    }) =>
      userManagementClient.removeUserFromUserGroup(userGroupId, targetUserId),
    onSuccess: (_, variables) => {
      queryClient.setQueryData(
        [USER_GROUPS_QUERY_KEY, variables.targetUserId],
        (oldData: UserManagement_V1.UserGroup[]) =>
          [...oldData].filter(
            (group) => group.userGroupId !== variables.userGroupId,
          ),
      );
      void queryClient.invalidateQueries({ queryKey: [USER_GROUPS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [USERS_IN_GROUP_QUERY_KEY, variables.userGroupId],
      });
      setIsRemoveFromGroupDialogOpen(false);
      setGroupToRemove(null);
    },
  });

  const handleRemoveFromGroup = useCallback((userGroup: UserGroupRow) => {
    setGroupToRemove(userGroup);
    setIsRemoveFromGroupDialogOpen(true);
  }, []);

  const { company } = useCompanyData(companyId);

  const formattedGroups = useMemo(() => {
    return userGroups.filter(x => x.companyId === company?.companyId).map<UserGroupRow>((group) => ({
      ...group,
      isDefaultGroup: !!group.isDefaultUserGroup,
      isPrimaryGroup: company?.defaultUserGroupId === group.userGroupId,
    }));
  }, [userGroups, company]);

  const handleRemoveFromGroupConfirm = () => {
    if (user.userId && groupToRemove?.userGroupId) {
      removeUserFromGroupMutation.mutate({
        userGroupId: groupToRemove.userGroupId,
        targetUserId: user.userId,
      });
    }
  };

  const handleRemoveFromGroupCancel = () => {
    setIsRemoveFromGroupDialogOpen(false);
    setGroupToRemove(null);
    removeUserFromGroupMutation.reset();
  };

  const renderGroupNameCell = useCallback(
    (ctx: CellContext<UserGroupRow, any>) => {
      return (
        <GroupNameCell ctx={ctx} isPrimaryGroup={ctx.row.original.isPrimaryGroup} />
      );
    },
    [],
  );

  const renderActionCell = (ctx: CellContext<UserGroupRow, any>) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const userGroup = ctx.row.original;
      handleRemoveFromGroup(userGroup);
    };

    const isPrimaryGroup = ctx.row.original.isPrimaryGroup;

    if (isPrimaryGroup) {
      return null;
    }

    return (
      <DeleteActionCell
        actionText={t("users.removeUser")}
        onClick={handleClick}
      />
    );
  };

  const groupsColumns = useMemo<ColumnDef<UserGroupRow, any>[]>(
    () => [
      {
        id: "name",
        header: t("users.groupTableHeaders.name"),
        accessorKey: "userGroupName",
        cell: renderGroupNameCell,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "text",
        size: 200,
      },
      {
        accessorKey: "userCount",
        header: t("users.groupTableHeaders.users"),
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        accessorKey: "experienceCount",
        header: t("users.groupTableHeaders.experiences"),
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: false,
        size: 100,
      },
      {
        id: "actions",
        header: undefined,
        cell: renderActionCell,
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [t],
  );

  return (
    <>
      <AddUserToGroupDialog
        companyId={companyId}
        isOpen={isAddUserToGroupDialogOpen}
        onClose={() => setIsAddUserToGroupDialogOpen(false)}
        userGroupsToExclude={userGroups}
        userId={user.userId}
      />

      <DataTable<UserGroupRow>
        actionButtonText={t("users.addToGroup")}
        actionButtonTextShort={t("users.add")}
        columns={groupsColumns}
        data={formattedGroups}
        defaultSort={INITIAL_SORTING}
        globalFilterPlaceholder={t("users.searchGroups")}
        onActionClick={() => setIsAddUserToGroupDialogOpen(true)}
        onRowClick={handleRowClick}
      />
      {/* Remove from Group Confirmation Dialog */}
      <ConfirmationDialog.Root
        aria-describedby="remove-from-group-dialog-description"
        aria-labelledby="remove-from-group-dialog-title"
        maxWidth="xs"
        onClose={handleRemoveFromGroupCancel}
        open={isRemoveFromGroupDialogOpen}
      >
        <ConfirmationDialog.Title
          iconColor={theme.colors.orange}
          id="remove-from-group-dialog-title"
          showIcon={true}
        >
          {t("users.removeUser")}
        </ConfirmationDialog.Title>
        <ConfirmationDialog.Content error={removeUserFromGroupMutation.error}>
          <ConfirmationDialog.Text id="remove-from-group-dialog-description">
            <Trans
              components={{ strong: <strong /> }}
              i18nKey="users.removeUserFromGroupConfirmation"
              values={{
                userName: `${user.surname}, ${user.forename}`,
                groupName: groupToRemove?.userGroupName,
              }}
            />
          </ConfirmationDialog.Text>
        </ConfirmationDialog.Content>
        <ConfirmationDialog.Actions>
          <ConfirmationDialog.Button
            onClick={handleRemoveFromGroupCancel}
            variant="contained"
          >
            {t("users.cancel")}
          </ConfirmationDialog.Button>
          <ConfirmationDialog.Button
            isLoading={removeUserFromGroupMutation.isPending}
            loadingText={t("users.removing")}
            onClick={handleRemoveFromGroupConfirm}
            variant="secondary"
          >
            {t("users.remove")}
          </ConfirmationDialog.Button>
        </ConfirmationDialog.Actions>
      </ConfirmationDialog.Root>
    </>
  );
};
